import React from 'react'
import { useNavigate } from 'react-router-dom'
import type { TenantThemeConfig } from '../types/theme'
import AssistiveFloatChrome from './AssistiveFloatChrome'
import { usePredictionUserEntry } from '../hooks/usePredictionUserEntry'
import './prediction/predictionModern.css'

interface DraggablePredictionFloatProps {
  themeConfig: TenantThemeConfig
  bottomOffset?: number
}

const FLOAT_SIZE = 58

function storageKey(tenantId: string) {
  return `prediction-float-pos:${tenantId}`
}

function getDefaultPosition(floatSize: number, bottomOffset: number) {
  if (typeof window === 'undefined') return { x: 16, y: 100 }
  return {
    x: Math.max(16, window.innerWidth - floatSize - 16),
    y: Math.max(16, window.innerHeight - floatSize - bottomOffset),
  }
}

const DraggablePredictionFloat: React.FC<DraggablePredictionFloatProps> = ({
  themeConfig,
  bottomOffset = 88,
}) => {
  const navigate = useNavigate()
  const { showPredictionEntry } = usePredictionUserEntry(themeConfig.settings.predictionEnabled)

  if (!showPredictionEntry) return null

  return (
    <AssistiveFloatChrome
      storageKey={storageKey(themeConfig.tenantId)}
      floatSize={FLOAT_SIZE}
      bottomOffset={bottomOffset}
      getDefaultPosition={getDefaultPosition}
      onTap={() => navigate('/points/games/prediction')}
      ariaLabel="เปิดหน้าทายผล Prediction"
      dockAriaLabel="ซ่อนปุ่มทายผลไว้ขอบจอ"
      undockAriaLabel="แสดงปุ่มทายผล"
      hideTitle="ซ่อนปุ่มทายผล"
      dockBackground="linear-gradient(180deg, #7c3aed 0%, #4f46e5 100%)"
      zIndex={9997}
      dockedChildren={(
        <>
          <i className="fas fa-bullseye" style={{ fontSize: 16 }} />
          <span style={{ fontSize: 8, fontWeight: 800, letterSpacing: 0.4 }}>ทายผล</span>
        </>
      )}
    >
      <div
        style={{
          width: '100%',
          height: '100%',
          borderRadius: '9999px',
          position: 'relative',
          background: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 45%, #4f46e5 100%)',
          boxShadow: '0 10px 28px rgba(124, 58, 237, 0.5)',
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
        }}
      >
        <span
          aria-hidden
          style={{
            position: 'absolute',
            inset: -4,
            borderRadius: '50%',
            background: 'conic-gradient(from 0deg, transparent, rgba(255,255,255,0.35), transparent 35%)',
            animation: 'predFloatRing 3s linear infinite',
          }}
        />
        <span
          style={{
            position: 'absolute',
            top: 8,
            right: 8,
            width: 9,
            height: 9,
            borderRadius: '50%',
            background: '#fde68a',
            animation: 'predFloatPulse 1.6s infinite',
          }}
        />
        <span style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', lineHeight: 1.05 }}>
          <i className="fas fa-bullseye" style={{ fontSize: 18, marginBottom: 2 }} />
          <span style={{ fontSize: 8, fontWeight: 800, letterSpacing: 0.4 }}>ทายผล</span>
        </span>
      </div>
    </AssistiveFloatChrome>
  )
}

export default DraggablePredictionFloat
